import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { LibraryFaq } from '../../types/library';

interface FaqSectionProps {
  faqs: LibraryFaq[];
  onAdd?: () => void;
  onEdit?: (faq: LibraryFaq) => void;
  onDelete?: (faq: LibraryFaq) => void;
  isAdmin: boolean;
}

const FaqSection: React.FC<FaqSectionProps> = ({ faqs, onAdd, onEdit, onDelete, isAdmin }) => {
  // Group FAQs by category
  const grouped = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {} as Record<string, LibraryFaq[]>);

  const categories = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Frequently Asked Questions</Typography>
        {isAdmin && onAdd && (
          <Tooltip title="Add FAQ">
            <IconButton onClick={onAdd} color="primary">
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {faqs.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No FAQs available yet
        </Typography>
      ) : (
        categories.map((category) => (
          <Box key={category} sx={{ mb: 3 }}>
            {categories.length > 1 && (
              <Chip label={category} size="small" sx={{ mb: 1 }} />
            )}
            {grouped[category].map((faq) => (
              <Accordion key={faq.id} variant="outlined" sx={{ mb: 0.5 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1 }}>
                    <Typography variant="body1" fontWeight={500} sx={{ flexGrow: 1 }}>
                      {faq.question}
                    </Typography>
                    {isAdmin && (
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={() => onEdit?.(faq)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete?.(faq)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))
      )}
    </Box>
  );
};

export default FaqSection;
